// src/utils/elqr.js
//
// Платёжные QR банков КР (стандарт ELQR: MBank, Optima, O!Bank и др.).
// Продавец один раз сохраняет СВОЙ пустой QR из банка, касса вписывает в него сумму чека.
//
// Формат (разобран по QR MBank, 12.10.2026, см. APK/mbankqr/):
//   https://app.mbank.kg/qr/#<payload>   (бывает и голый payload без ссылки)
//   payload — TLV как в EMVCo: тег(2) + длина(2 цифры) + значение.
//   00=01 | 01=11 | 32=[00=c2c.mbank.kg, 01=02, 10=<телефон>, 13=12] | 52=9999 | 53=417 | 59=<имя> | 63=<контроль>
//   С суммой: 54=<сумма в тыйынах> после 53 (123 сом → "12300"); у MBank в 32 ещё 12=11 и 13=11 вместо 13=12.
//   63 у MBank = последние 4 hex SHA-256 от payload ДО "6304" (не CRC16 из EMVCo).
// Пересборка из пустого QR MBank совпала со всеми 6 образцами банка байт в байт.
// O!Bank (13.10.2026, APK/obankqr/): https://payqr.kg/#<payload>, тот же контроль SHA-256 (hex ЗАГЛАВНЫМИ).
//   32=[00=p2p.dengi.kg, 01=8580, 11=<id>, 10=<телефон>, 12=11|12, 13=12] | 34=<имя> | 52=7399 | 53=417 | 54=0 | 59=O!Bank
//   С суммой: 54=<тыйыны>, в 32 12=12 (без суммы 12=11). Имя получателя — в 34, а в 59 название банка.
// Для других банков алгоритм контроля определяем по их же QR (sha256 или crc16);
// формат суммы у них не проверен — продавцу в настройках предлагается тест на 1 сом.

// ── SHA-256 (синхронно: QR рисуется прямо в рендере) ──
const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function sha256Hex(str) {
  const bytes = new TextEncoder().encode(str);
  const len = bytes.length;
  const total = Math.ceil((len + 9) / 64) * 64;
  const buf = new Uint8Array(total);
  buf.set(bytes);
  buf[len] = 0x80;
  const dv = new DataView(buf.buffer);
  dv.setUint32(total - 4, (len * 8) >>> 0);
  dv.setUint32(total - 8, Math.floor(len / 0x20000000));
  const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
  const w = new Uint32Array(64);
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < total; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i++) {
      const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i] + w[i]) >>> 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] += a; h[1] += b; h[2] += c; h[3] += d; h[4] += e; h[5] += f; h[6] += g; h[7] += hh;
  }
  return Array.from(h, x => x.toString(16).padStart(8, '0')).join('');
}

// CRC16-CCITT (0xFFFF) — контроль из стандарта EMVCo, на случай если другой банк делает по нему
function crc16(str) {
  let c = 0xffff;
  for (const b of new TextEncoder().encode(str)) {
    c ^= b << 8;
    for (let i = 0; i < 8; i++) c = (c & 0x8000) ? ((c << 1) ^ 0x1021) & 0xffff : (c << 1) & 0xffff;
  }
  return c.toString(16).padStart(4, '0');
}

const CHECKS = {
  sha256: (body) => sha256Hex(body).slice(-4),
  crc16: (body) => crc16(body + '6304'),
};

// Длина значения считается в символах (у банков — латиница, цифры, пробел)
function parseTlv(s) {
  const out = [];
  let i = 0;
  while (i < s.length) {
    const tag = s.slice(i, i + 2);
    const len = Number(s.slice(i + 2, i + 4));
    if (!/^\d{2}$/.test(tag) || !Number.isFinite(len) || i + 4 + len > s.length) return null;
    out.push([tag, s.slice(i + 4, i + 4 + len)]);
    i += 4 + len;
  }
  return out;
}

const serTlv = (items) => items.map(([t, v]) => t + String(v.length).padStart(2, '0') + v).join('');

// Сырой текст из QR → { prefix, payload } (payload раскодирован из %20)
function splitRaw(raw) {
  const s = String(raw || '').trim();
  const hash = s.indexOf('#');
  const prefix = /^https?:\/\//i.test(s) && hash > 0 ? s.slice(0, hash + 1) : '';
  let payload = prefix ? s.slice(hash + 1) : s;
  try { payload = decodeURIComponent(payload); } catch (e) { /* оставляем как есть */ }
  return { prefix, payload };
}

const BANKS = [
  { re: /mbank/i, name: 'MBank' },
  { re: /optima/i, name: 'Optima' },
  { re: /dengi|obank|payqr\.kg/i, name: 'O!Bank' },
  { re: /bakai/i, name: 'Bakai' },
  { re: /demir/i, name: 'Demir' },
  { re: /kicb/i, name: 'KICB' },
  { re: /rsk/i, name: 'РСК' },
  { re: /aiyl|ab\.kg/i, name: 'Айыл Банк' },
  { re: /elqr|elcart/i, name: 'ELQR' },
];

// Разбор сохраняемого QR. null — это не платёжный QR.
// → { raw, bank, recipient, phone, check: 'sha256'|'crc16'|null, hasAmount }
export function parsePayQr(raw) {
  const { prefix, payload } = splitRaw(raw);
  const items = parseTlv(payload);
  if (!items || !items.length) return null;
  const map = Object.fromEntries(items);
  if (map['00'] !== '01' || !map['63'] || items[items.length - 1][0] !== '63') return null;
  const body = payload.slice(0, payload.length - 8);
  const check = Object.keys(CHECKS).find(k => CHECKS[k](body).toLowerCase() === map['63'].toLowerCase()) || null;

  // Счёт получателя — первый шаблон 26..51 с GUI в 00
  const accTag = items.find(([t]) => Number(t) >= 26 && Number(t) <= 51);
  const acc = accTag ? Object.fromEntries(parseTlv(accTag[1]) || []) : {};
  const where = `${prefix} ${acc['00'] || ''}`;
  const bank = (BANKS.find(b => b.re.test(where)) || {}).name || '';
  const phone = Object.values(acc).find(v => /^996\d{9}$/.test(v)) || '';

  // У O!Bank имя получателя в 34, а в 59 — название банка
  const recipient = map['34'] || map['59'] || '';
  return { raw: String(raw).trim(), bank, recipient, phone, check, hasAmount: Number(map['54']) > 0 };
}

// QR продавца + сумма (сом) → текст QR для клиента. null — вписать сумму нельзя (неизвестный контроль).
export function payQrWithAmount(raw, amount) {
  const info = parsePayQr(raw);
  if (!info || !info.check) return null;
  const { prefix, payload } = splitRaw(raw);
  const tyiyn = Math.round(Number(amount) * 100);
  if (!(tyiyn > 0)) return null;

  const out = [];
  for (const [tag, val] of parseTlv(payload)) {
    if (tag === '54' || tag === '63') continue;
    let v = val;
    // MBank: у QR с суммой в счёте 12=11 и 13=11 (у пустого 13=12)
    if (/^(2[6-9]|[34]\d|5[01])$/.test(tag) && /mbank/i.test(val)) {
      const sub = (parseTlv(val) || []).filter(([t]) => t !== '12' && t !== '13');
      v = serTlv([...sub, ['12', '11'], ['13', '11']]);
    }
    // O!Bank: у QR с суммой в счёте 12=12 (у пустого 12=11), порядок полей сохраняем
    if (/^(2[6-9]|[34]\d|5[01])$/.test(tag) && /dengi/i.test(val)) {
      v = serTlv((parseTlv(val) || []).map(([t, x]) => [t, t === '12' ? '12' : x]));
    }
    out.push([tag, v]);
    if (tag === '53') out.push(['54', String(tyiyn)]);
  }
  if (!out.some(([t]) => t === '54')) return null;   // нет валюты (53) — некуда вписать сумму
  const body = serTlv(out);
  // Регистр контроля — как в QR банка (MBank строчными, O!Bank заглавными)
  const was = parseTlv(payload).find(([t]) => t === '63')[1];
  const upper = /[A-F]/.test(was) || (!/[a-f]/.test(was) && /dengi/i.test(payload));
  const sum = CHECKS[info.check](body);
  const full = body + '6304' + (upper ? sum.toUpperCase() : sum);
  return prefix ? prefix + encodeURI(full).replace(/!/g, '%21') : full;
}
